import React, { useState, useEffect, useRef } from 'react';
import { supabaseAdmin } from '../lib/supabaseClient'; 
import { Project, ProjectDB } from '../types';
import { safeUrl } from '../constants';

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null); // Fix #7: Add error state

  const [filter, setFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Search State
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filters = ['All', 'Web Dev', 'Mobile Apps', 'Design Systems', 'Open Source', 'AI'];

  useEffect(() => {
    // Load search history from localStorage
    try {
      const savedHistory = localStorage.getItem('projectSearchHistory');
      if (savedHistory) {
        setSearchHistory(JSON.parse(savedHistory));
      }
    } catch (error) {
      console.error('Error loading search history:', error);
      setSearchHistory([]);
    }
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const saveToHistory = (query: string) => {
    if (!query.trim()) return;

    const newHistory = [query, ...searchHistory.filter(h => h !== query)].slice(0, 5);
    setSearchHistory(newHistory);

    try {
      localStorage.setItem('projectSearchHistory', JSON.stringify(newHistory));
    } catch (error) {
      console.error('Error saving search history:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      saveToHistory(searchQuery);
      searchInputRef.current?.blur();
      setIsSearchFocused(false);
    }
  };

  const clearHistory = () => {
    setSearchHistory([]);

    try {
      localStorage.removeItem('projectSearchHistory');
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  };

  // FETCH DATA FROM SUPABASE
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch data from the 'projects' table
        const { data, error: fetchError } = await supabaseAdmin
          .from('projects')
          .select('*')
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;

        if (data) {
          // Fix #15: Type assertion with ProjectDB interface instead of 'any'
          const dbData = data as ProjectDB[];

          // Convert Database format (snake_case) to Frontend format (camelCase)
          const formattedProjects: Project[] = dbData.map((item) => ({
            id: item.id.toString(),
            title: item.title,
            description: item.description,
            tags: item.tech_stack || [], // Database: tech_stack -> UI: tags
            imageUrl: item.image_url,    // Database: image_url -> UI: imageUrl
            category: item.project_type as "Web Dev" | "Mobile Apps" | "Design Systems" | "Open Source" | "AI", // Database: project_type -> UI: category
            demoUrl: item.demo_link || '', 
            codeUrl: item.repo_link || '#',
            status: item.status as "Planning" | "In Progress" | "Done",
          }));
          
          setProjects(formattedProjects);
        }
      } catch (err: unknown) {
        // Fix #7: Proper error handling display
        console.error('Error fetching projects:', err);
        setError('Failed to load projects. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Reset page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery]);

  const filteredProjects = projects.filter(project => {
    const matchesCategory = filter === 'All' || project.category === filter;
    
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = !query || 
      project.title.toLowerCase().includes(query) ||
      project.description.toLowerCase().includes(query) ||
      project.tags.some(tag => tag.toLowerCase().includes(query));

    return matchesCategory && matchesSearch;
  });
  
  // Pagination Logic
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedProjects = filteredProjects.slice(startIndex, startIndex + itemsPerPage);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Done': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'In Progress': return 'bg-primary/10 text-primary border-primary/20';
      case 'Planning': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  return (
    <div className="flex-grow">
      <div className="w-full max-w-[1200px] mx-auto flex flex-col">
        {/* Header */}
        <section className="px-4 md:px-8 py-12 md:py-20 flex flex-col items-center justify-center text-center animate-fade-in-up">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter mb-4 text-slate-900 dark:text-white">
              Projects
            </h1>
            <p className="text-slate-600 dark:text-gray-400 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl mx-auto">
              A curated portfolio of high-impact solutions. Featuring functional applications and automated systems built with modern technologies like Kotlin, React, and Lua. This is where ideas transform into reality.
            </p>
          </div>
        </section>

        {/* Fix #7: Error State UI */}
        {error && (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <span className="material-symbols-outlined text-4xl text-red-500 mb-2">error</span>
                <p className="text-red-500 font-bold">{error}</p>
            </div>
        )}

        {/* Loading State */}
        {loading && !error ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : !loading && !error ? (
          <>
            {/* Advanced Search Bar */}
            <section className="px-4 md:px-8 mb-8 animate-fade-in-up relative z-50" style={{ animationDelay: '0.1s' }}>
              <div className="max-w-md mx-auto relative z-50">
                <div className={`relative flex items-center w-full h-14 bg-white dark:bg-[#1a2633] border rounded-full transition-all duration-300 ${isSearchFocused ? 'border-primary shadow-lg ring-1 ring-primary/20' : 'border-gray-200 dark:border-gray-700 shadow-sm hover:border-primary dark:hover:border-primary hover:shadow-sm'}`}>
                  <div className="pl-5 flex items-center pointer-events-none">
                    <span className="material-symbols-outlined text-slate-400">search</span>
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="block w-full pl-3 pr-10 py-3 bg-transparent border-none rounded-full leading-5 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-0"
                    placeholder="Search projects by title, or keywords..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                    onKeyDown={handleKeyDown}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    >
                      <span className="material-symbols-outlined text-xl">close</span>
                    </button>
                  )}
                </div>

                {/* Search History Dropdown */}
                {isSearchFocused && searchHistory.length > 0 && (
                  <div className={`absolute top-full left-0 w-full mt-2 bg-white dark:bg-[#1a2633] rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xl p-4 z-50 transition-all duration-300 origin-top ${isSearchFocused ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                    <div className="flex justify-between items-center mb-3 px-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Recent Searches</span>
                      <button onClick={clearHistory} className="text-[10px] text-red-400 hover:text-red-500 font-bold hover:underline transition-colors">Clear History</button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {searchHistory.map((historyItem, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setSearchQuery(historyItem);
                            setIsSearchFocused(false);
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-surface-dark border border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:bg-white dark:hover:bg-[#233648] text-xs font-medium text-slate-600 dark:text-slate-300 transition-all active:scale-95"
                        >
                          <span className="material-symbols-outlined text-[14px] text-slate-400">history</span>
                          {historyItem}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Filter */}
            <section className="px-4 md:px-8 mb-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {filters.map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 ${
                      filter === f
                        ? 'bg-primary text-slate-900 shadow-lg shadow-primary/20'
                        : 'bg-white dark:bg-[#233648] hover:bg-gray-100 dark:hover:bg-[#2f455a] border border-gray-200 dark:border-transparent text-slate-700 dark:text-white'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </section>

            {/* Projects Grid */}
            <section className="px-4 md:px-8 py-8 mb-20 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              {displayedProjects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {displayedProjects.map((project: Project) => (
                    <article key={project.id} className="group flex flex-col bg-white dark:bg-[#1a2633] rounded-2xl overflow-hidden border border-gray-200 dark:border-[#2a3b4d] hover:border-primary/50 dark:hover:border-primary/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-2">
                      <div className="relative w-full aspect-video overflow-hidden">
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors z-10 duration-500"></div>
                        <div 
                          className="w-full h-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                          style={{ backgroundImage: `url("${project.imageUrl}")` }}
                        ></div>
                      </div>
                      <div className="p-6 flex flex-col flex-grow">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-xl font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors">{project.title}</h3>
                        </div>
                        <p className="text-slate-600 dark:text-gray-400 text-sm mb-4 line-clamp-2 flex-grow">
                          {project.description}
                        </p>
                        <div className="flex flex-col gap-3 mb-6">
                          <div className="flex flex-wrap gap-2">
                            {project.tags.map((tag, i) => (
                              <span key={i} className="px-2.5 py-1 rounded bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-200 text-[10px] font-bold uppercase tracking-wider">{tag}</span>
                            ))}
                          </div>
                          <div className={`self-start px-2.5 py-1 rounded border text-[10px] font-black uppercase tracking-widest ${getStatusColor(project.status)}`}>
                            {project.status}
                          </div>
                        </div>
                        <div className="flex gap-3 mt-auto">
                          {/* Fix #11: Use safeUrl() to sanitize links */}
                          {project.demoUrl && (
                            <a href={safeUrl(project.demoUrl)} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-2 h-10 rounded-lg bg-primary hover:bg-primary-dark text-slate-900 text-sm font-bold transition-all duration-300 hover:shadow-lg hover:shadow-primary/20">
                              <span className="material-symbols-outlined text-[18px]">visibility</span>
                              Demo
                            </a>
                          )}
                          <a href={safeUrl(project.codeUrl)} target="_blank" rel="noreferrer" className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-lg bg-gray-100 dark:bg-[#233648] hover:bg-gray-200 dark:hover:bg-[#2f455a] text-gray-900 dark:text-white text-sm font-bold transition-colors ${!project.demoUrl ? 'w-full' : ''}`}>
                            <span className="material-symbols-outlined text-[18px]">code</span>
                            {project.demoUrl ? 'Code' : 'View on GitHub'}
                          </a>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-gray-50 dark:bg-[#1a2633] rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                  <span className="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600 mb-4">search_off</span>
                  <p className="text-xl font-bold text-slate-600 dark:text-slate-400">No projects found matching your criteria.</p>
                  <p className="text-slate-500 dark:text-slate-500 text-sm mb-6">Try different keywords or change the category filter.</p>
                  <button 
                    onClick={() => { setFilter('All'); setSearchQuery(''); }}
                    className="px-6 py-2.5 bg-primary text-slate-900 font-bold rounded-lg text-sm hover:bg-primary-dark transition-colors shadow-lg shadow-primary/20"
                  >
                    Clear Filters & Search
                  </button>
                </div>
              )}

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-12">
                   <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="size-10 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-[#233648] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-slate-600 dark:text-slate-300"
                  >
                    <span className="material-symbols-outlined text-sm">arrow_back_ios_new</span>
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`size-10 flex items-center justify-center rounded-lg text-sm font-bold transition-all ${
                        currentPage === pageNum
                          ? 'bg-primary text-slate-900 shadow-md'
                          : 'bg-transparent border border-gray-200 dark:border-gray-700 text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-[#233648]'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}

                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="size-10 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-[#233648] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-slate-600 dark:text-slate-300"
                  >
                    <span className="material-symbols-outlined text-sm">arrow_forward_ios</span>
                  </button>
                </div>
              )}
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Projects;